import { getAdminEmbedSession } from '../adminEmbed';

// API Client Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.venuelista.com/api';

// Token management
const TOKEN_KEY = 'venuelista_token';

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Custom error class for API errors
export class ApiError extends Error {
  status: number;
  data: unknown;
  /** Laravel-style per-field validation errors: { field: ["msg", ...] } */
  validationErrors: Record<string, string[]>;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    // Extract Laravel validation errors from data if present
    this.validationErrors = {};
    if (typeof data === 'object' && data !== null && 'errors' in data) {
      const errObj = (data as { errors: unknown }).errors;
      if (typeof errObj === 'object' && errObj !== null) {
        this.validationErrors = errObj as Record<string, string[]>;
      }
    }
  }

  /** Get a flat summary of all validation error messages */
  get validationMessage(): string {
    const msgs: string[] = [];
    for (const field of Object.keys(this.validationErrors)) {
      const fieldErrors = this.validationErrors[field];
      if (Array.isArray(fieldErrors)) {
        msgs.push(...fieldErrors);
      }
    }
    return msgs.length > 0 ? msgs.join('. ') : this.message;
  }

  /** Get the first error for a specific field */
  getFieldError(field: string): string | undefined {
    const errs = this.validationErrors[field];
    return Array.isArray(errs) && errs.length > 0 ? errs[0] : undefined;
  }
}

// Request options type
interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  requireAuth?: boolean;
  /** When true, do not send Authorization even if a token exists (public endpoints). */
  omitAuth?: boolean;
  /** When true, send body as-is (FormData) and omit Content-Type so the browser sets multipart boundary. */
  isFormData?: boolean;
}

// API Response wrapper
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

// Generic fetch wrapper with auth and error handling
async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    body,
    requireAuth = false,
    omitAuth = false,
    isFormData = false,
    headers: customHeaders,
    ...fetchOptions
  } = options;

  const headers: HeadersInit = {
    Accept: 'application/json',
    ...customHeaders,
  };
  if (!isFormData) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  // Add auth token if required or available
  const embed = getAdminEmbedSession();
  const token = embed?.token ?? getToken();
  if (embed?.venueId) (headers as Record<string, string>)['X-Venue-Id'] = embed.venueId;
  if (embed?.memberId) (headers as Record<string, string>)['X-Member-Id'] = embed.memberId;
  if (token && !omitAuth) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  } else if (requireAuth) {
    throw new ApiError('Authentication required', 401);
  }

  const config: RequestInit = {
    ...fetchOptions,
    headers,
  };

  if (body instanceof FormData) {
    config.body = body;
  } else if (body) {
    config.body = JSON.stringify(body);
  }

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, config);

    // Read the body as text first: a malformed/empty body must never turn a real HTTP status
    // (502 with a provider reason, 204 with no content, …) into a fake "network error".
    const contentType = response.headers.get('content-type') ?? '';
    const rawBody = await response.text().catch(() => '');

    let data: unknown = rawBody;
    if (rawBody !== '' && contentType.includes('application/json')) {
      try {
        data = JSON.parse(rawBody);
      } catch {
        // Not valid JSON — keep the raw text so callers still see the true status.
        data = rawBody;
      }
    }

    if (!response.ok) {
      let message = `Request failed with status ${response.status}`;
      if (typeof data === 'object' && data !== null) {
        const obj = data as Record<string, unknown>;
        if ('message' in obj && typeof obj.message === 'string') {
          message = obj.message;
        }
        // Append field-level validation errors if present (Laravel-style)
        if ('errors' in obj && typeof obj.errors === 'object' && obj.errors !== null) {
          const fieldMsgs: string[] = [];
          for (const msgs of Object.values(obj.errors as Record<string, string[]>)) {
            if (Array.isArray(msgs)) fieldMsgs.push(...msgs);
          }
          if (fieldMsgs.length > 0) {
            message = fieldMsgs.join('. ');
          }
        }
      }
      throw new ApiError(message, response.status, data);
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

// Client-side uploads use XHR (not fetch) because only XHR reports the bytes actually sent.

/** Progress sample emitted while a multipart upload streams to the API. */
export type UploadProgress = {
  loaded: number;
  /** 0 when the browser cannot compute the total (proxies that hide Content-Length). */
  total: number;
  /** 0 – 1; stays 0 while `total` is unknown. */
  ratio: number;
  bytesPerSecond: number;
  etaSeconds: number | null;
  /** Body fully sent, waiting on the server (re-encode / storage) — render an indeterminate state. */
  uploadComplete: boolean;
};

export type UploadRequestOptions = {
  requireAuth?: boolean;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
};

function errorFromResponse(status: number, data: unknown): ApiError {
  let message = `Request failed with status ${status}`;
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    if ('message' in obj && typeof obj.message === 'string') {
      message = obj.message;
    }
    if ('errors' in obj && typeof obj.errors === 'object' && obj.errors !== null) {
      const fieldMsgs: string[] = [];
      for (const msgs of Object.values(obj.errors as Record<string, string[]>)) {
        if (Array.isArray(msgs)) fieldMsgs.push(...msgs);
      }
      if (fieldMsgs.length > 0) message = fieldMsgs.join('. ');
    }
  }
  return new ApiError(message, status, data);
}

/**
 * Multipart POST with upload progress.
 *
 * `fetch()` cannot report how many bytes have been sent, so gallery uploads (photos + videos)
 * go through XMLHttpRequest. Everything else mirrors `request()`: same base URL, bearer auth,
 * JSON parsing and `ApiError` shape, so callers can swap between the two freely.
 */
export function postFormWithProgress<T>(
  endpoint: string,
  formData: FormData,
  options: UploadRequestOptions = {},
): Promise<T> {
  const { requireAuth = false, onProgress, signal } = options;

  const embed = getAdminEmbedSession();
  const token = embed?.token ?? getToken();
  if (!token && requireAuth) {
    return Promise.reject(new ApiError('Authentication required', 401));
  }

  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError('Upload cancelled', 0));
      return;
    }

    const xhr = new XMLHttpRequest();
    let lastLoaded = 0;
    let lastTick = Date.now();
    let bytesPerSecond = 0; // exponentially smoothed

    const emit = (progress: UploadProgress) => onProgress?.(progress);

    const handleAbort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener('abort', handleAbort);

    xhr.open('POST', `${API_BASE_URL}${endpoint}`);
    xhr.setRequestHeader('Accept', 'application/json');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (embed?.venueId) xhr.setRequestHeader('X-Venue-Id', embed.venueId);
    if (embed?.memberId) xhr.setRequestHeader('X-Member-Id', embed.memberId);
    signal?.addEventListener('abort', handleAbort);

    xhr.upload.onprogress = (event) => {
      const now = Date.now();
      const elapsed = (now - lastTick) / 1000;

      if (elapsed > 0 && event.loaded >= lastLoaded) {
        const instant = (event.loaded - lastLoaded) / elapsed;
        bytesPerSecond = bytesPerSecond > 0 ? bytesPerSecond * 0.7 + instant * 0.3 : instant;
      }

      lastLoaded = event.loaded;
      lastTick = now;

      const total = event.lengthComputable ? event.total : 0;
      const ratio = total > 0 ? Math.min(1, event.loaded / total) : 0;

      emit({
        loaded: event.loaded,
        total,
        ratio,
        bytesPerSecond,
        etaSeconds: bytesPerSecond > 0 && total > event.loaded ? (total - event.loaded) / bytesPerSecond : null,
        uploadComplete: false,
      });
    };

    // Bytes are on the wire; the server still has to validate + store them.
    xhr.upload.onload = () => {
      emit({
        loaded: lastLoaded,
        total: lastLoaded,
        ratio: 1,
        bytesPerSecond,
        etaSeconds: null,
        uploadComplete: true,
      });
    };

    xhr.onload = () => {
      cleanup();

      const contentType = xhr.getResponseHeader('content-type') ?? '';
      let data: unknown = null;

      if (xhr.responseText) {
        if (contentType.includes('application/json')) {
          try {
            data = JSON.parse(xhr.responseText);
          } catch {
            data = xhr.responseText;
          }
        } else {
          data = xhr.responseText;
        }
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data as T);
      } else {
        reject(errorFromResponse(xhr.status, data));
      }
    };

    xhr.onerror = () => {
      cleanup();
      reject(new ApiError('Network error', 0));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new ApiError('Upload timed out', 0));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new ApiError('Upload cancelled', 0));
    };

    xhr.send(formData);
  });
}

// API client with methods for each HTTP verb
export const apiClient = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) => {
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      body,
      isFormData: body instanceof FormData,
    });
  },

  put: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body,
      isFormData: body instanceof FormData,
    }),

  postForm: <T>(endpoint: string, formData: FormData, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(endpoint, { ...options, method: 'POST', body: formData, isFormData: true }),

  putForm: <T>(endpoint: string, formData: FormData, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(endpoint, { ...options, method: 'PUT', body: formData, isFormData: true }),

  patch: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default apiClient;
